import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TimeSlot } from "@/hooks/useTimeSlots";
import { countOverlappingBookings, BookedRangeWithCapacity } from "@/utils/timeSlotUtils";
import { localDateTimeToUTC } from "@/utils/timezoneUtils";

export const useAvailableTimeSlots = (
  serviceId?: string, 
  date?: Date, 
  isProviderViewing = false, 
  providerId?: string
) => {
  return useQuery({
    queryKey: ['available-time-slots', serviceId, date?.toISOString(), isProviderViewing, providerId],
    queryFn: async () => {
      if (!date) {
        console.log('useAvailableTimeSlots: Missing date', { serviceId, date, providerId });
        return [];
      }
      
      // When provider is viewing all services, we need providerId
      if (isProviderViewing && !serviceId && !providerId) {
        console.log('useAvailableTimeSlots: Provider viewing all services but no providerId provided');
        return [];
      }
      
      // For customer booking, serviceId is required
      if (!isProviderViewing && !serviceId) {
        console.log('useAvailableTimeSlots: Customer booking requires serviceId');
        return [];
      }
      
      // Get the date string in local timezone (YYYY-MM-DD format)
      const localDateString = date.toLocaleDateString('en-CA');
      
      // Create start and end of day in UTC for the selected local date
      const startOfDayUTC = localDateTimeToUTC(localDateString, '00:00:00');
      const endOfDayUTC = localDateTimeToUTC(localDateString, '23:59:59');

      console.log('useAvailableTimeSlots: Query parameters', {
        serviceId,
        selectedLocalDate: localDateString,
        startOfDayUTC,
        endOfDayUTC,
        currentTime: new Date().toISOString(),
        isProviderViewing
      });

      // Get all available time slots for the service/provider on the selected date (time slots are stored in UTC)
      let timeSlotsQuery = supabase
        .from('time_slots')
        .select(`
          *,
          service:services(id, name, duration_minutes, reserved_duration_minutes, room_id),
          room:rooms(id, name)
        `)
        .eq('is_available', true)
        .gte('start_time', startOfDayUTC)
        .lte('start_time', endOfDayUTC)
        .order('start_time', { ascending: true });
      
      // Filter by service if specified, otherwise filter by provider (for "all services" view)
      if (serviceId) {
        timeSlotsQuery = timeSlotsQuery.eq('service_id', serviceId);
      } else if (providerId) {
        timeSlotsQuery = timeSlotsQuery.eq('provider_id', providerId);
      }
      
      const { data: timeSlots, error: timeSlotsError } = await timeSlotsQuery;

      if (timeSlotsError) {
        console.error('Error fetching time slots:', timeSlotsError);
        throw timeSlotsError;
      }

      console.log('useAvailableTimeSlots: Time slots found', { 
        count: timeSlots?.length,
        localDateString,
        isProviderViewing,
        timeSlots: timeSlots?.map(slot => ({
          id: slot.id,
          start_time: slot.start_time,
          end_time: slot.end_time,
          current_bookings: slot.current_bookings,
          max_bookings: slot.max_bookings,
          is_available: slot.is_available
        }))
      });

      // PROVIDER VIEWING MODE - Simplified logic but still filter past slots
      if (isProviderViewing === true) {
        console.log('useAvailableTimeSlots: PROVIDER VIEW MODE - using simplified filtering with time check');
        
        const now = new Date();
        const availableSlots: TimeSlot[] = [];
        
        for (const slot of timeSlots || []) {
          // Time slots are stored in UTC, so we can compare directly
          const slotStartTime = new Date(slot.start_time);
          const isMarkedAvailable = slot.is_available;
          const isNotPast = slotStartTime > now;

          console.log('useAvailableTimeSlots: Provider view - evaluating slot', {
            slotId: slot.id,
            slotStartTime: slot.start_time,
            slotStartTimeLocal: slotStartTime.toLocaleString(),
            currentTime: now.toISOString(),
            currentTimeLocal: now.toLocaleString(),
            isNotPast,
            currentBookings: slot.current_bookings,
            maxBookings: slot.max_bookings,
            isMarkedAvailable,
            spotsRemaining: slot.max_bookings - slot.current_bookings
          });

          // For provider viewing, show slots that are marked as available AND not in the past
          if (isMarkedAvailable && isNotPast) {
            availableSlots.push(slot as TimeSlot);
          }
        }
        
        console.log('useAvailableTimeSlots: Provider view - final filtered slots', {
          localDateString,
          totalSlots: timeSlots?.length || 0,
          availableSlots: availableSlots.length,
          slots: availableSlots.map(slot => ({
            id: slot.id,
            start_time: slot.start_time,
            end_time: slot.end_time,
            start_time_local: new Date(slot.start_time).toLocaleString(),
            current_bookings: slot.current_bookings,
            max_bookings: slot.max_bookings,
            spotsLeft: slot.max_bookings - slot.current_bookings
          }))
        });
        
        return availableSlots;
      }

      // CUSTOMER BOOKING MODE - Full conflict detection and availability logic
      console.log('useAvailableTimeSlots: CUSTOMER BOOKING MODE - using full conflict detection');

      // Get service details to know the duration and room
      const { data: service, error: serviceError } = await supabase
        .from('services')
        .select('duration_minutes, reserved_duration_minutes, room_id, provider_id')
        .eq('id', serviceId)
        .single();

      if (serviceError) {
        console.error('Error fetching service details:', serviceError);
        throw serviceError;
      }

      // Fetch provider's min_booking_advance_minutes setting
      const { data: providerData, error: providerError } = await supabase
        .from('providers')
        .select('min_booking_advance_minutes')
        .eq('id', service.provider_id)
        .single();

      if (providerError) {
        console.error('Error fetching provider details:', providerError);
        // Continue without the setting if it fails (default to 0)
      }

      const minBookingAdvanceMinutes = providerData?.min_booking_advance_minutes || 0;

      // Calculate total duration including reserved time
      const totalDurationMinutes = (service.duration_minutes || 0) + (service.reserved_duration_minutes || 0);
      console.log('useAvailableTimeSlots: Service details', {
        serviceDuration: service.duration_minutes,
        reservedDuration: service.reserved_duration_minutes,
        totalDuration: totalDurationMinutes,
        roomId: service.room_id,
        minBookingAdvanceMinutes
      });

      // Use the secure RPC function to get booked ranges (bypasses RLS)
      const { data: bookedRanges, error: bookedRangesError } = await supabase
        .rpc('get_room_booked_ranges', {
          p_provider_id: service.provider_id,
          p_booking_date: localDateString
        });

      if (bookedRangesError) {
        console.error('Error fetching booked ranges via RPC:', bookedRangesError);
        throw bookedRangesError;
      }

      // Get room capacity for the service's room
      let roomCapacity = 1;
      if (service.room_id) {
        const { data: room } = await supabase
          .from('rooms')
          .select('capacity')
          .eq('id', service.room_id)
          .single();
        roomCapacity = room?.capacity || 1;
      }

      // Convert RPC result to typed array
      const existingBookedRanges: BookedRangeWithCapacity[] = (bookedRanges || []).map((range: { start_time: string; end_time: string; room_id: string | null; room_capacity: number }) => ({
        start_time: range.start_time,
        end_time: range.end_time,
        room_id: range.room_id,
        room_capacity: range.room_capacity
      }));

      console.log('useAvailableTimeSlots: Booked ranges from RPC', {
        localDateString,
        serviceRoomId: service.room_id,
        roomCapacity,
        bookedRangesCount: existingBookedRanges.length,
        bookedRanges: existingBookedRanges.map(r => ({
          start: r.start_time,
          end: r.end_time,
          room_id: r.room_id,
          room_capacity: r.room_capacity
        }))
      });

      // Filter out unavailable slots based on multiple criteria for CUSTOMER BOOKING
      const now = new Date();
      // Calculate cutoff time based on provider's minimum advance booking requirement
      const minAdvanceMs = minBookingAdvanceMinutes * 60 * 1000;
      const cutoffTime = new Date(now.getTime() + minAdvanceMs);
      
      console.log('useAvailableTimeSlots: Current time for customer filtering:', {
        now: now.toISOString(),
        minBookingAdvanceMinutes,
        cutoffTime: cutoffTime.toISOString()
      });
      
      const availableSlots: TimeSlot[] = [];
      
      for (const slot of timeSlots || []) {
        // Time slots are stored in UTC, so we can compare directly
        const slotStartTime = new Date(slot.start_time);
        const slotEndTime = new Date(slot.end_time);
        const isNotFullyBooked = slot.current_bookings < slot.max_bookings;
        const isNotPast = slotStartTime > now;
        const meetsAdvanceRequirement = slotStartTime > cutoffTime;
        const isMarkedAvailable = slot.is_available;

        console.log('useAvailableTimeSlots: Customer booking - evaluating slot availability', {
          slotId: slot.id,
          slotStartTime: slot.start_time,
          slotEndTime: slot.end_time,
          currentBookings: slot.current_bookings,
          maxBookings: slot.max_bookings,
          isNotFullyBooked,
          isNotPast,
          meetsAdvanceRequirement,
          isMarkedAvailable,
          spotsRemaining: slot.max_bookings - slot.current_bookings
        });

        // Primary availability check: time slot booking capacity, basic checks, and advance requirement
        if (!isNotFullyBooked || !isNotPast || !meetsAdvanceRequirement || !isMarkedAvailable) {
          console.log('useAvailableTimeSlots: Customer booking - basic availability check failed', {
            slotId: slot.id,
            isNotFullyBooked,
            isNotPast,
            meetsAdvanceRequirement,
            isMarkedAvailable
          });
          continue;
        }

        // Calculate the end time for the proposed booking (slot start + service duration)
        const proposedBookingEndTime = new Date(slotStartTime.getTime() + totalDurationMinutes * 60000);
        const proposedBookingEndTimeString = proposedBookingEndTime.toISOString().split('T')[1].split('.')[0]; // HH:MM:SS format
        const proposedBookingStartTimeString = slotStartTime.toISOString().split('T')[1].split('.')[0]; // HH:MM:SS format

        console.log('useAvailableTimeSlots: Customer booking - checking for capacity conflicts', {
          slotId: slot.id,
          proposedBookingStart: proposedBookingStartTimeString,
          proposedBookingEnd: proposedBookingEndTimeString,
          serviceDuration: totalDurationMinutes,
          roomId: service.room_id,
          roomCapacity,
          existingBookedRangesCount: existingBookedRanges.length
        });

        // Count overlapping bookings in the same room
        const overlappingCount = countOverlappingBookings(
          {
            start_time: proposedBookingStartTimeString,
            end_time: proposedBookingEndTimeString,
            room_id: service.room_id
          },
          existingBookedRanges
        );

        // Block only if room is at capacity
        const isAtCapacity = overlappingCount >= roomCapacity;

        if (isAtCapacity) {
          console.log('useAvailableTimeSlots: Customer booking - room at capacity for slot', {
            slotId: slot.id,
            proposedTime: `${proposedBookingStartTimeString}-${proposedBookingEndTimeString}`,
            overlappingCount,
            roomCapacity,
            overlappingBookings: existingBookedRanges.filter(booking => {
              const roomMatch = !service.room_id || !booking.room_id || service.room_id === booking.room_id;
              const timeOverlap = proposedBookingStartTimeString < booking.end_time && proposedBookingEndTimeString > booking.start_time;
              return roomMatch && timeOverlap;
            }).map(b => `${b.start_time}-${b.end_time}`)
          });
          continue;
        }

        console.log('useAvailableTimeSlots: Customer booking - slot passes all checks', {
          slotId: slot.id,
          slotStartTime: slot.start_time,
          currentBookings: slot.current_bookings,
          maxBookings: slot.max_bookings,
          spotsLeft: slot.max_bookings - slot.current_bookings,
          proposedBookingEnd: proposedBookingEndTimeString,
          isAvailable: true
        });
        
        // Add the slot since it passes all checks
        availableSlots.push(slot as TimeSlot);
      }
      
      console.log('useAvailableTimeSlots: Customer booking - final filtered slots', {
        localDateString,
        totalSlots: timeSlots?.length || 0,
        availableSlots: availableSlots.length,
        slots: availableSlots.map(slot => ({
          id: slot.id,
          start_time: slot.start_time,
          end_time: slot.end_time,
          start_time_local: new Date(slot.start_time).toLocaleString(),
          current_bookings: slot.current_bookings,
          max_bookings: slot.max_bookings,
          spotsLeft: slot.max_bookings - slot.current_bookings,
          is_available: slot.is_available
        }))
      });
      
      return availableSlots;
    },
    enabled: !!date && (!!serviceId || (isProviderViewing && !!providerId)),
    // Add real-time updates
    refetchInterval: 10000, // Reduced to 10 seconds for more frequent updates
    refetchOnWindowFocus: true,
  });
};
